<!-- Scene Editor Toolbar -->
<script lang="ts">
  import {
    editorMode,
    setEditorMode,
    undo,
    redo,
    sceneState,
    setGrid,
    setSnap,
  } from '../stores/sceneEditorStore';
  import Button from '@/ui/base/Button.svelte';
  import Icon from '@/ui/base/Icon.svelte';
  import { onMount, onDestroy } from 'svelte';

  const modes = [
    {
      id: 'select',
      icon: 'cursor',
      label: 'Select',
      description: 'Select and move objects',
      shortcut: 'V',
    },
    {
      id: 'move',
      icon: 'move',
      label: 'Move',
      description: 'Move objects with gizmo handles',
      shortcut: 'G',
    },
    {
      id: 'scale',
      icon: 'scale',
      label: 'Scale',
      description: 'Scale objects with corner handles',
      shortcut: 'S',
    },
    {
      id: 'rotate',
      icon: 'rotate',
      label: 'Rotate',
      description: 'Rotate objects with rotation handle',
      shortcut: 'R',
    },
    {
      id: 'pan',
      icon: 'pan',
      label: 'Pan',
      description: 'Pan the viewport',
      shortcut: 'H',
    },
  ] as const;

  function handleModeChange(mode: string) {
    setEditorMode(mode as any);
  }

  function handleUndo() {
    undo();
  }

  function handleRedo() {
    redo();
  }

  function handleToggleGrid() {
    const current = $sceneState;
    setGrid(!current.grid.enabled);
  }

  function handleToggleSnap() {
    const current = $sceneState;
    setSnap(!current.snap.enabled);
  }

  // Keyboard shortcuts
  function handleKeydown(event: KeyboardEvent) {
    // Ignore if user is typing in an input
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
      return;
    }

    switch (event.key.toLowerCase()) {
      case 'v':
        setEditorMode('select');
        event.preventDefault();
        break;
      case 'g':
        setEditorMode('move');
        event.preventDefault();
        break;
      case 's':
        setEditorMode('scale');
        event.preventDefault();
        break;
      case 'r':
        setEditorMode('rotate');
        event.preventDefault();
        break;
      case 'h':
        setEditorMode('pan');
        event.preventDefault();
        break;
      case ' ':
        // Space for temporary pan mode
        if (!event.repeat) {
          setEditorMode('pan');
          event.preventDefault();
        }
        break;
      case 'z':
        if (event.ctrlKey || event.metaKey) {
          if (event.shiftKey) {
            redo();
          } else {
            undo();
          }
          event.preventDefault();
        }
        break;
      case 'y':
        if (event.ctrlKey || event.metaKey) {
          redo();
          event.preventDefault();
        }
        break;
    }
  }

  onMount(() => {
    window.addEventListener('keydown', handleKeydown);
  });

  onDestroy(() => {
    window.removeEventListener('keydown', handleKeydown);
  });
</script>

<div
  class="toolbar flex items-center gap-4 p-4 bg-gray-800/90 backdrop-blur-sm border-b border-neon-purple/20"
>
  <!-- Mode Selector -->
  <div class="flex gap-2">
    {#each modes as mode}
      <div class="group relative">
        <Button
          variant={$editorMode === mode.id ? 'primary' : 'secondary'}
          size="sm"
          on:click={() => handleModeChange(mode.id)}
          title={`${mode.description} (${mode.shortcut})`}
          className="group"
        >
          <Icon name={mode.icon} size="sm" />
          <span class="hidden sm:inline">{mode.label}</span>
          <span class="hidden sm:inline text-xs opacity-60 ml-1">({mode.shortcut})</span>
        </Button>

        <!-- Tooltip -->
        <div
          class="absolute top-full left-1/2 transform -translate-x-1/2 mt-6 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10"
        >
          {mode.description}
          <div
            class="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-900"
          ></div>
        </div>
      </div>
    {/each}
  </div>

  <!-- Divider -->
  <div class="w-px h-6 bg-gray-600"></div>

  <!-- History Controls -->
  <div class="flex gap-2">
    <Button variant="secondary" size="sm" on:click={handleUndo}>
      <Icon name="undo" size="sm" />
      Undo
    </Button>
    <Button variant="secondary" size="sm" on:click={handleRedo}>
      <Icon name="redo" size="sm" />
      Redo
    </Button>
  </div>

  <!-- Divider -->
  <div class="w-px h-6 bg-gray-600"></div>

  <!-- Grid Toggle -->
  <Button
    variant={$sceneState.grid.enabled ? 'primary' : 'secondary'}
    size="sm"
    on:click={handleToggleGrid}
  >
    <Icon name="grid" size="sm" />
    Grid
  </Button>

  <!-- Snap Toggle -->
  <Button
    variant={$sceneState.snap.enabled ? 'primary' : 'secondary'}
    size="sm"
    on:click={handleToggleSnap}
  >
    <Icon name="snap" size="sm" />
    Snap
  </Button>
</div>
